import type { MouseEventHandler, ReactNode } from 'react';
import React from 'react';
import { Link as RRDLink } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import type { JSX as EmotionJSX } from '@emotion/react/jsx-runtime';

export interface LinkProps {
  messageId?: string | ReactNode;
  url?: string;
  plainText?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  download?: string;
  children?: ReactNode;
  openInNewWindow?: boolean;
  /** Names an icon-only link; its subtree name differs between Chrome and jsdom. */
  'aria-label'?: string;
}

const Link: React.FC<LinkProps> = ({
  messageId,
  url,
  plainText = false,
  onClick,
  className,
  children,
  openInNewWindow,
  ...props
}) => {
  const LinkText = children ? children : plainText ? messageId : <FormattedMessage id={messageId as string} />;

  if (url?.startsWith('/') && !openInNewWindow) {
    return (
      <StyledRRDLink to={url} className={className} {...props} onClick={onClick}>
        {LinkText}
      </StyledRRDLink>
    );
  }
  return (
    <StyledLink
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      href={url}
      onClick={e => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={className}>
      {LinkText}
    </StyledLink>
  );
};

/* One treatment for external and internal alike. The doubled class beats antd's global `a:hover`, whose
   cssinjs tag is injected after emotion's. */
export const linkCss = css`
  color: var(--text-tint-lime);
  font-weight: var(--type-weight-label);

  &&:hover {
    color: var(--text-tint-lime);
    text-decoration: underline;
  }
`;

const StyledLink = styled.a`
  ${linkCss}
`;

const StyledRRDLink = styled(RRDLink)`
  ${linkCss}
`;

export const setLink = (link: string, onClick?: () => void) => buildLink(link, { onClick });

export const buildLink = (link: string, props?: LinkProps) => ({
  a: (text: string | ReactNode) => <Link plainText messageId={text} url={link} {...props} />
});

export const setLinksByTag = (tagToLinkMap: Record<string, string>) => {
  const tagToLinkCallback: Record<string, (text: string | ReactNode) => EmotionJSX.Element> = {};

  Object.keys(tagToLinkMap).forEach(tag => {
    tagToLinkCallback[tag] = text => <Link messageId={text} url={tagToLinkMap[tag] ?? ''} plainText />;
  });

  return tagToLinkCallback;
};

export default Link;
